import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { StatusBadge } from '../components/StatusBadge';
import { Modal } from '../components/Modal';
import { FilterBar } from '../components/FilterBar';
import { ClassContentPicker } from '../components/ClassContentPicker';
import { useAuth } from '../context/AuthContext';
import type { ManagedUser } from '../types/auth';
import type { SchoolClass } from '../types/class';
import type { ClassContentSelection } from '../types/classActivityAssignment';
import {
  EMPTY_CLASS_CONTENT_SELECTION,
  selectionFromAssignments
} from '../types/classActivityAssignment';

const PAGE_SIZE = 10;

type ClassModalTab = 'details' | 'content';

export function ClassesPage() {
  const { user, isSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const schoolId = user?.schoolId || searchParams.get('schoolId') || null;

  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [teachers, setTeachers] = useState<ManagedUser[]>([]);
  const [students, setStudents] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SchoolClass | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editTab, setEditTab] = useState<ClassModalTab>('details');

  const [editName, setEditName] = useState('');
  const [editGrade, setEditGrade] = useState('');
  const [editTeacherId, setEditTeacherId] = useState('');
  const [editStudentIds, setEditStudentIds] = useState<string[]>([]);
  const [editContent, setEditContent] = useState<ClassContentSelection>(
    EMPTY_CLASS_CONTENT_SELECTION
  );
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createTab, setCreateTab] = useState<ClassModalTab>('details');
  const [createName, setCreateName] = useState('');
  const [createGrade, setCreateGrade] = useState('');
  const [createTeacherId, setCreateTeacherId] = useState('');
  const [createContent, setCreateContent] = useState<ClassContentSelection>(
    EMPTY_CLASS_CONTENT_SELECTION
  );
  const [creating, setCreating] = useState(false);

  const teacherById = useMemo(() => {
    const map = new Map<string, ManagedUser>();
    teachers.forEach((t) => map.set(t.id, t));
    return map;
  }, [teachers]);

  const gradeOptions = useMemo(() => {
    const set = new Set<string>();
    classes.forEach((item) => {
      if (item.grade?.trim()) set.add(item.grade.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [classes]);

  const filteredClasses = useMemo(() => {
    const q = search.trim().toLowerCase();
    const grade = gradeFilter.trim().toLowerCase();
    return classes.filter((item) => {
      if (grade && (item.grade ?? '').toLowerCase() !== grade) return false;
      if (!q) return true;
      const teacherName = item.teacherUserId
        ? teacherById.get(item.teacherUserId)?.fullName ?? ''
        : '';
      const haystack = `${item.name} ${item.grade ?? ''} ${teacherName}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [classes, search, gradeFilter, teacherById]);

  const loadRoster = useCallback(async (sid: string) => {
    const [teacherResult, studentResult] = await Promise.all([
      api.listUsers({ role: 'TEACHER', schoolId: sid, limit: 100 }),
      api.listUsers({ role: 'STUDENT', schoolId: sid, limit: 100 })
    ]);
    setTeachers(teacherResult.items);
    setStudents(studentResult.items);
  }, []);

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    try {
      const [items] = await Promise.all([
        api.listClasses(schoolId, { activeOnly: activeOnly || undefined }),
        loadRoster(schoolId)
      ]);
      setClasses(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [schoolId, activeOnly, loadRoster]);

  useEffect(() => {
    void loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (!schoolId || !selectedId || !editOpen) {
      setSelected(null);
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      try {
        const [classDoc, assignments] = await Promise.all([
          api.getClass(schoolId!, selectedId!),
          api.listClassActivityAssignments({ classId: selectedId! })
        ]);
        if (!cancelled) {
          setSelected(classDoc);
          setEditName(classDoc.name);
          setEditGrade(classDoc.grade ?? '');
          setEditTeacherId(classDoc.teacherUserId ?? '');
          setEditStudentIds(classDoc.studentUserIds);
          setEditContent(selectionFromAssignments(assignments));
          setEditTab('details');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load class');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedId, editOpen]);

  const totalPages = Math.max(1, Math.ceil(filteredClasses.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filteredClasses.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = filteredClasses.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredClasses.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  function openEdit(classId: string) {
    setSelectedId(classId);
    setEditOpen(true);
    setEditTab('details');
    setError(null);
    setSuccess(null);
  }

  function closeEdit() {
    if (saving) return;
    setEditOpen(false);
    setSelectedId(null);
    setSelected(null);
    setEditContent(EMPTY_CLASS_CONTENT_SELECTION);
    setEditTab('details');
  }

  function openCreate() {
    setShowCreate(true);
    setCreateTab('details');
    setCreateContent(EMPTY_CLASS_CONTENT_SELECTION);
    setError(null);
    setSuccess(null);
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    if (!schoolId) return;
    if (!createName.trim()) {
      setCreateTab('details');
      setError('Class name is required');
      return;
    }
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await api.createClass(schoolId, {
        name: createName.trim(),
        grade: createGrade.trim() || undefined,
        teacherUserId: createTeacherId || null
      });

      if (
        createContent.characterPillarIds.length > 0 ||
        createContent.missionIds.length > 0 ||
        createContent.learningLectureIds.length > 0 ||
        createContent.homeActivityIds.length > 0
      ) {
        await api.replaceClassActivityAssignments(created.id, createContent);
      }

      setClasses((current) => [created, ...current]);
      setCreateName('');
      setCreateGrade('');
      setCreateTeacherId('');
      setCreateContent(EMPTY_CLASS_CONTENT_SELECTION);
      setCreateTab('details');
      setShowCreate(false);
      setSuccess(`Created class ${created.name}.`);
      openEdit(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create class');
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!schoolId || !selected) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      let updated = await api.updateClass(schoolId, selected.id, {
        name: editName.trim(),
        grade: editGrade.trim() || null
      });

      const nextTeacher = editTeacherId || null;
      if ((selected.teacherUserId ?? null) !== nextTeacher) {
        updated = await api.assignClassTeacher(schoolId, selected.id, nextTeacher);
      }

      const sameStudents =
        selected.studentUserIds.length === editStudentIds.length &&
        selected.studentUserIds.every((id) => editStudentIds.includes(id));
      if (!sameStudents) {
        updated = await api.setClassStudents(schoolId, selected.id, editStudentIds);
      }

      await api.replaceClassActivityAssignments(selected.id, editContent);

      setSelected(updated);
      setClasses((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSuccess('Class saved.');
      setEditOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save class');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(classDoc: SchoolClass) {
    if (!schoolId) return;
    setBusyId(classDoc.id);
    setError(null);
    setSuccess(null);
    try {
      const updated = classDoc.isActive
        ? await api.deactivateClass(schoolId, classDoc.id)
        : await api.reactivateClass(schoolId, classDoc.id);
      setClasses((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      if (selected?.id === updated.id) setSelected(updated);
      setSuccess(`${updated.name} is now ${updated.isActive ? 'active' : 'inactive'}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update class status');
    } finally {
      setBusyId(null);
    }
  }

  function toggleStudent(studentId: string) {
    setEditStudentIds((current) =>
      current.includes(studentId)
        ? current.filter((id) => id !== studentId)
        : [...current, studentId]
    );
  }

  if (!schoolId) {
    return (
      <section className="admin-panel">
        <div className="panel-heading">
          <div>
            <h2>Classes</h2>
            <p>
              {isSuperAdmin
                ? 'Open a school from Schools and pass schoolId, or sign in as a school admin.'
                : 'Your account is not linked to a school yet.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>Classes</h2>
          <p>
            Create classes, assign a teacher, enroll students, choose available content, and manage
            class lifecycle.
          </p>
        </div>
        <span className="panel-meta">{filteredClasses.length} shown</span>
      </div>

      <FilterBar
        className="classes-toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <label>
          Search
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, grade, or teacher"
          />
        </label>
        <label>
          Grade
          <select
            value={gradeFilter}
            onChange={(e) => {
              setPage(1);
              setGradeFilter(e.target.value);
            }}
          >
            <option value="">All grades</option>
            {gradeOptions.map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </label>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setPage(1);
              setActiveOnly(e.target.checked);
            }}
          />
          Active only
        </label>
        <button type="submit" className="primary-btn">
          Apply
        </button>
        <button type="button" className="ghost-btn" onClick={() => void loadClasses()}>
          Refresh
        </button>
        <button type="button" className="ghost-btn" onClick={openCreate}>
          New class
        </button>
      </FilterBar>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="form-success" role="status">
          {success}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading classes…</p> : null}
      {!loading && filteredClasses.length === 0 ? (
        <p className="muted">No classes match these filters.</p>
      ) : null}

      {!loading && filteredClasses.length > 0 ? (
        <div className="school-list-panel">
          <div className="table-wrap school-list-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Grade</th>
                  <th>Teacher</th>
                  <th>Students</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((classDoc) => (
                  <tr key={classDoc.id}>
                    <td>
                      <strong>{classDoc.name}</strong>
                    </td>
                    <td>{classDoc.grade || '—'}</td>
                    <td>
                      {classDoc.teacherUserId
                        ? teacherById.get(classDoc.teacherUserId)?.fullName ||
                          classDoc.teacherUserId
                        : '—'}
                    </td>
                    <td>{classDoc.studentUserIds.length}</td>
                    <td>
                      <StatusBadge status={classDoc.isActive ? 'active' : 'suspended'} />
                    </td>
                    <td>
                      <div className="action-row">
                        <button
                          type="button"
                          className="ghost-btn compact"
                          onClick={() => openEdit(classDoc.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ghost-btn compact"
                          disabled={busyId === classDoc.id}
                          onClick={() => void handleToggleActive(classDoc)}
                        >
                          {classDoc.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="school-list-pager" role="navigation" aria-label="Class list pagination">
            <span className="school-list-pager-range">
              {rangeStart}-{rangeEnd} of {filteredClasses.length}
            </span>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Previous page"
              disabled={safePage <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ‹
            </button>
            <button
              type="button"
              className="pager-chevron"
              aria-label="Next page"
              disabled={safePage >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              ›
            </button>
          </div>
        </div>
      ) : null}

      <Modal
        open={showCreate}
        title="Create class"
        subtitle="Name is required. Optionally assign a teacher and choose available content."
        size="lg"
        onClose={() => {
          if (!creating) {
            setShowCreate(false);
            setCreateTab('details');
            setCreateContent(EMPTY_CLASS_CONTENT_SELECTION);
          }
        }}
        closeDisabled={creating}
        asForm
        onSubmit={(event) => void handleCreate(event)}
        actions={
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={creating}
              onClick={() => {
                setShowCreate(false);
                setCreateTab('details');
                setCreateContent(EMPTY_CLASS_CONTENT_SELECTION);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={creating}>
              {creating ? 'Creating…' : 'Create class'}
            </button>
          </>
        }
      >
        <div className="tab-bar" role="tablist" aria-label="Create class sections">
          <button
            type="button"
            className={`tab-btn${createTab === 'details' ? ' active' : ''}`}
            role="tab"
            aria-selected={createTab === 'details'}
            onClick={() => setCreateTab('details')}
          >
            Details
          </button>
          <button
            type="button"
            className={`tab-btn${createTab === 'content' ? ' active' : ''}`}
            role="tab"
            aria-selected={createTab === 'content'}
            onClick={() => setCreateTab('content')}
          >
            Content
          </button>
        </div>
        {createTab === 'details' ? (
          <div className="form-grid">
            <label>
              Name
              <input required value={createName} onChange={(e) => setCreateName(e.target.value)} />
            </label>
            <label>
              Grade
              <input
                value={createGrade}
                onChange={(e) => setCreateGrade(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label>
              Teacher
              <select value={createTeacherId} onChange={(e) => setCreateTeacherId(e.target.value)}>
                <option value="">Unassigned</option>
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.fullName} ({teacher.status})
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <ClassContentPicker
            value={createContent}
            onChange={setCreateContent}
            disabled={creating}
          />
        )}
      </Modal>

      <Modal
        open={editOpen}
        title={selected?.name || 'Edit class'}
        subtitle={
          selected
            ? `${selected.isActive ? 'Active' : 'Inactive'} · ${selected.studentUserIds.length} students`
            : undefined
        }
        size="lg"
        onClose={closeEdit}
        closeDisabled={saving}
        asForm
        onSubmit={(event) => void handleSave(event)}
        actions={
          <>
            <button type="button" className="ghost-btn" disabled={saving} onClick={closeEdit}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={saving || detailLoading}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        {detailLoading ? <p className="muted">Loading class…</p> : null}
        {selected && !detailLoading ? (
          <>
            <div className="tab-bar" role="tablist" aria-label="Edit class sections">
              <button
                type="button"
                className={`tab-btn${editTab === 'details' ? ' active' : ''}`}
                role="tab"
                aria-selected={editTab === 'details'}
                onClick={() => setEditTab('details')}
              >
                Details
              </button>
              <button
                type="button"
                className={`tab-btn${editTab === 'content' ? ' active' : ''}`}
                role="tab"
                aria-selected={editTab === 'content'}
                onClick={() => setEditTab('content')}
              >
                Content
              </button>
            </div>
            {editTab === 'details' ? (
              <div className="stacked-form">
                <label>
                  Name
                  <input required value={editName} onChange={(e) => setEditName(e.target.value)} />
                </label>
                <label>
                  Grade
                  <input value={editGrade} onChange={(e) => setEditGrade(e.target.value)} />
                </label>
                <label>
                  Teacher
                  <select value={editTeacherId} onChange={(e) => setEditTeacherId(e.target.value)}>
                    <option value="">Unassigned</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName} ({teacher.status})
                      </option>
                    ))}
                  </select>
                </label>

                <div className="enrollment-block">
                  <h4>Students enrolled</h4>
                  {students.length === 0 ? (
                    <p className="muted">No students in this school yet. Add them under Users.</p>
                  ) : (
                    <div className="enrollment-list">
                      {students.map((student) => (
                        <label key={student.id} className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={editStudentIds.includes(student.id)}
                            onChange={() => toggleStudent(student.id)}
                          />
                          <span>
                            {student.fullName}
                            <span className="cell-sub">{student.email}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <ClassContentPicker
                value={editContent}
                onChange={setEditContent}
                disabled={saving}
              />
            )}
          </>
        ) : null}
      </Modal>
    </section>
  );
}
